import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PaymentPlanForm } from './PaymentPlanForm'
import { Edit, Trash2 } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'

interface PaymentPlanDetailModalProps {
  paymentPlan: any
  isOpen: boolean
  onClose: () => void
  onUpdate: (id: string, updates: any) => Promise<void>
  onDelete: (id: string) => Promise<void>
  updating?: boolean
  deleting?: boolean
}

export function PaymentPlanDetailModal({
  paymentPlan,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  updating,
  deleting,
}: PaymentPlanDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)

  const handleUpdate = async (data: any) => {
    await onUpdate(paymentPlan.id, data)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete this payment plan? This action cannot be undone.`)) {
      return
    }

    await onDelete(paymentPlan.id)
    onClose()
  }

  if (isEditing) {
    return (
      <Modal isOpen={isOpen} onClose={() => setIsEditing(false)} title="Edit Payment Plan" size="lg">
        <PaymentPlanForm
          initialData={paymentPlan}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          loading={updating}
        />
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Payment Plan Details" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Total Amount</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(paymentPlan.total_amount)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Installment Amount</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(paymentPlan.installment_amount)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Frequency</p>
            <Badge>{paymentPlan.frequency}</Badge>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Status</p>
            <Badge variant={paymentPlan.status === 'active' ? 'success' : 'default'}>
              {paymentPlan.status}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Start Date</p>
            <p className="text-sm text-gray-900">{formatDate(paymentPlan.start_date)}</p>
          </div>
          {paymentPlan.end_date && (
            <div>
              <p className="text-sm font-medium text-gray-500">End Date</p>
              <p className="text-sm text-gray-900">{formatDate(paymentPlan.end_date)}</p>
            </div>
          )}
        </div>

        {paymentPlan.notes && (
          <div>
            <p className="text-sm font-medium text-gray-500">Notes</p>
            <p className="text-sm text-gray-900">{paymentPlan.notes}</p>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t">
          <Button variant="danger" onClick={handleDelete} loading={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <div className="flex space-x-3">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
